import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCategoryIcon } from "@/lib/categoryIcons";
import { resolveAssetUrl } from "@/lib/media";

interface Post {
  title: string;
  slug: string;
  cover_image: string;
  excerpt: string;
  author: string;
  published_at: string;
  category: string;
  arabic_title?: string | null;
  arabic_excerpt?: string | null;
}

interface RelatedPostsProps {
  posts: Post[];
}

export function RelatedPosts({ posts }: RelatedPostsProps) {
  const { t, lang, localePath } = useLanguage();
  if (posts.length === 0) return null;

  return (
    <section className="mt-16 pt-12 border-t border-border">
      <h2 className="text-2xl font-display font-bold text-foreground mb-8">
        {t("Related Articles", "مقالات ذات صلة")}
      </h2>
      <div className="flex flex-col gap-4">
        {posts.map((post) => {
          const displayTitle = lang === "ar" && post.arabic_title ? post.arabic_title : post.title;
          const displayExcerpt = lang === "ar" && post.arabic_excerpt ? post.arabic_excerpt : post.excerpt;
          const imageSrc = resolveAssetUrl(post.cover_image);
          const formattedDate = new Date(post.published_at).toLocaleDateString(
            lang === "ar" ? "ar-SA" : "en-US",
            { year: "numeric", month: "short", day: "numeric" }
          );

          return (
            <Link
              key={post.slug}
              to={localePath(`/blog/${post.slug}`)}
              className="group flex flex-col sm:flex-row gap-4 sm:gap-5 p-3 rounded-xl border border-border hover:border-primary/30 bg-card transition-all duration-300 hover:shadow-glow"
            >
              <div className="sm:w-48 md:w-56 flex-shrink-0 overflow-hidden rounded-lg bg-secondary/50">
                <img
                  src={imageSrc}
                  alt={displayTitle}
                  className="w-full h-full aspect-[16/11] sm:aspect-auto object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-col justify-center py-1 min-w-0">
                {post.category && (() => {
                  const CatIcon = getCategoryIcon(post.category);
                  return (
                    <Badge variant="secondary" className="text-xs w-fit mb-2 inline-flex items-center gap-1">
                      <CatIcon size={12} weight="duotone" />
                      {post.category}
                    </Badge>
                  );
                })()}
                <h3 className="font-sans font-medium text-base group-hover:text-primary transition-colors mb-1 line-clamp-2">
                  {displayTitle}
                </h3>
                {displayExcerpt && (
                  <p className="text-sm text-muted-foreground line-clamp-1 leading-relaxed mb-2">
                    {displayExcerpt}
                  </p>
                )}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{post.author}</span>
                  <span>·</span>
                  <span>{formattedDate}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
